import React, { useEffect, useRef, useState } from "react";

/**
 * "NIGHT PATROL" — roam a glowing, neon-lit NYC street as Earth Jr.
 * Fight waves of trash-bots, grab sidekick power-ups, defeat the giant Trash-Bot boss.
 * Desktop: WASD/arrows + SPACE. Mobile: virtual joystick (left) + FIRE button (right).
 */

const W = 420;
const H = 560;
const WORLD_W = 2600;
const STREET_TOP = 335; // player can roam between here and street bottom
const STREET_BOT = H - 34;

type Vec = { x: number; y: number };
type Bot = {
  x: number; y: number; hp: number; maxHp: number; speed: number;
  kind: "tv" | "can" | "battery"; hit: number; wob: number; boss?: boolean;
  attackCd: number;
};
type Shot = { x: number; y: number; vx: number; vy: number; life: number };
type Puff = { x: number; y: number; vx: number; vy: number; life: number; max: number; color: string; size: number };
type Drop = { x: number; y: number; kind: "sunny" | "drip" | "windy" | "bin"; life: number };
type Phase = "ready" | "playing" | "won" | "lost";

const DROP_EMOJI: Record<Drop["kind"], string> = { sunny: "☀️", drip: "💧", windy: "🌬️", bin: "♻️" };
const DROP_LABEL: Record<Drop["kind"], string> = { sunny: "SUNNY: RAPID FIRE!", drip: "DRIP: +1 HEART!", windy: "WINDY: SHOCKWAVE!", bin: "BIN: SHIELD UP!" };

// ---- deterministic scenery (computed once) ----
function seeded(n: number) { const x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); }

type Building = { x: number; w: number; h: number; hue: number; windows: { wx: number; wy: number; on: boolean }[]; sign?: { text: string; color: string; y: number } };
const NEON = ["#22e58f", "#3ea6ff", "#ff7a45", "#ffd23e", "#c86bff"];
const SIGNS = ["ECO", "♻ RECYCLE", "BODEGA", "PIZZA", "REPAIR", "DONATE", "24HR", "GREEN", "NYC"];

function makeBuildings(layer: number, count: number, minH: number, maxH: number): Building[] {
  const out: Building[] = [];
  let x = -80;
  for (let i = 0; i < count; i++) {
    const s = seeded(i * 7.3 + layer * 91);
    const w = 70 + s * 90;
    const h = minH + seeded(i * 3.1 + layer * 17) * (maxH - minH);
    const b: Building = { x, w, h, hue: seeded(i + layer * 5) , windows: [] };
    const cols = Math.max(2, (w / 22) | 0), rows = Math.max(3, (h / 26) | 0);
    for (let c = 0; c < cols; c++) for (let r = 0; r < rows; r++) {
      if (seeded(i * 13 + c * 7 + r * 3 + layer) > 0.45) {
        b.windows.push({ wx: 8 + c * ((w - 16) / cols), wy: 10 + r * ((h - 20) / rows), on: seeded(i + c * 11 + r * 29) > 0.35 });
      }
    }
    if (layer === 1 && seeded(i * 31 + 4) > 0.45) {
      b.sign = { text: SIGNS[(seeded(i * 3) * SIGNS.length) | 0], color: NEON[(seeded(i * 5) * NEON.length) | 0], y: 26 + seeded(i * 9) * 40 };
    }
    out.push(b);
    x += w + 6 + seeded(i * 2.2) * 30;
    if (x > WORLD_W + 200) break;
  }
  return out;
}
const FAR = makeBuildings(0, 60, 120, 210);
const NEAR = makeBuildings(1, 60, 150, 260);
const LAMPS: number[] = []; for (let i = 0; i < 14; i++) LAMPS.push(120 + i * 190);
const STARS: Vec[] = []; for (let i = 0; i < 60; i++) STARS.push({ x: seeded(i * 1.7) * W, y: seeded(i * 3.9) * 180 });

const EcoNightPatrol: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<Phase>("ready");
  const [finalScore, setFinalScore] = useState(0);

  const g = useRef({
    running: false,
    px: 200, py: 420, face: 1, walk: 0, moving: false,
    hearts: 3, invuln: 0, shield: 0, rapid: 0,
    keys: {} as Record<string, boolean>,
    joy: { active: false, id: -1, ox: 0, oy: 0, dx: 0, dy: 0 },
    firePointer: -1, firing: false, fireCd: 0,
    bots: [] as Bot[], shots: [] as Shot[], puffs: [] as Puff[], drops: [] as Drop[],
    wave: 1, toSpawn: 0, spawnT: 0, boss: null as Bot | null,
    score: 0, cam: 0, t: 0, shake: 0, toast: "", toastT: 0, waveT: 0,
  });

  const start = () => {
    const s = g.current;
    s.px = 200; s.py = 420; s.face = 1; s.hearts = 3; s.invuln = 0; s.shield = 0; s.rapid = 0;
    s.bots = []; s.shots = []; s.puffs = []; s.drops = []; s.boss = null;
    s.wave = 1; s.toSpawn = 4; s.spawnT = 30; s.score = 0; s.cam = 0; s.t = 0;
    s.shake = 0; s.toast = "WAVE 1 — HERE THEY COME!"; s.toastT = 90; s.waveT = 0;
    s.fireCd = 0; s.firing = false; s.keys = {};
    s.running = true;
    setPhase("playing");
  };

  // keyboard
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const s = g.current;
      if (!s.running) return;
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " "].includes(e.key)) e.preventDefault();
      s.keys[e.key.toLowerCase()] = true;
    };
    const up = (e: KeyboardEvent) => { g.current.keys[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", down, { passive: false });
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  // touch joystick + fire button
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const toLocal = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: ((e.clientX - r.left) / r.width) * W, y: ((e.clientY - r.top) / r.height) * H };
    };
    const pd = (e: PointerEvent) => {
      const s = g.current;
      if (!s.running) return;
      const p = toLocal(e);
      if (p.x < W / 2 && p.y > H * 0.5) {
        s.joy = { active: true, id: e.pointerId, ox: p.x, oy: p.y, dx: 0, dy: 0 };
      } else {
        s.firePointer = e.pointerId; s.firing = true;
      }
      canvas.setPointerCapture(e.pointerId);
    };
    const pm = (e: PointerEvent) => {
      const s = g.current;
      if (s.joy.active && e.pointerId === s.joy.id) {
        const p = toLocal(e);
        const dx = p.x - s.joy.ox, dy = p.y - s.joy.oy;
        const d = Math.hypot(dx, dy) || 1, m = Math.min(d, 34);
        s.joy.dx = (dx / d) * (m / 34); s.joy.dy = (dy / d) * (m / 34);
      }
    };
    const pu = (e: PointerEvent) => {
      const s = g.current;
      if (e.pointerId === s.joy.id) s.joy = { active: false, id: -1, ox: 0, oy: 0, dx: 0, dy: 0 };
      if (e.pointerId === s.firePointer) { s.firing = false; s.firePointer = -1; }
    };
    canvas.addEventListener("pointerdown", pd);
    canvas.addEventListener("pointermove", pm);
    canvas.addEventListener("pointerup", pu);
    canvas.addEventListener("pointercancel", pu);
    return () => {
      canvas.removeEventListener("pointerdown", pd);
      canvas.removeEventListener("pointermove", pm);
      canvas.removeEventListener("pointerup", pu);
      canvas.removeEventListener("pointercancel", pu);
    };
  }, []);

  // game loop
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    const game = g.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.scale(dpr, dpr);
    let raf = 0;

    const puffBurst = (x: number, y: number, color: string, n = 10, spd = 3) => {
      const s = g.current;
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2, sp = 0.6 + Math.random() * spd;
        s.puffs.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 0.6, life: 34, max: 34, color, size: 2 + Math.random() * 3.5 });
      }
    };

    const spawnBot = (nearBoss = false) => {
      const s = g.current;
      const side = Math.random() > 0.5 ? 1 : -1;
      const bx = nearBoss && s.boss
        ? s.boss.x + (Math.random() - 0.5) * 120
        : Math.min(WORLD_W - 40, Math.max(40, s.px + side * (W * 0.62 + Math.random() * 160)));
      const kinds: Bot["kind"][] = ["tv", "can", "battery"];
      const hp = 2 + ((s.wave / 2) | 0);
      s.bots.push({
        x: bx, y: STREET_TOP + Math.random() * (STREET_BOT - STREET_TOP - 20),
        hp, maxHp: hp, speed: 0.55 + Math.random() * 0.5 + s.wave * 0.08,
        kind: kinds[(Math.random() * 3) | 0], hit: 0, wob: Math.random() * 7, attackCd: 0,
      });
    };

    const hurtPlayer = () => {
      const s = g.current;
      if (s.invuln > 0) return;
      if (s.shield > 0) { s.shield = 0; s.invuln = 50; s.toast = "SHIELD BLOCKED IT!"; s.toastT = 60; puffBurst(s.px, s.py, "#22e58f", 14); return; }
      s.hearts--; s.invuln = 70; s.shake = 12;
      puffBurst(s.px, s.py, "#e23b3b", 14);
      if (s.hearts <= 0) { s.running = false; setFinalScore(s.score); setPhase("lost"); }
    };

    const killBot = (b: Bot, idx: number) => {
      const s = g.current;
      puffBurst(b.x, b.y, "#3ea6ff", 16, 4);
      puffBurst(b.x, b.y, "#ffd23e", 8, 2);
      s.score += b.boss ? 500 : 25;
      s.bots.splice(idx, 1);
      if (!b.boss && Math.random() < 0.26) {
        const kinds: Drop["kind"][] = ["sunny", "drip", "windy", "bin"];
        s.drops.push({ x: b.x, y: b.y, kind: kinds[(Math.random() * 4) | 0], life: 620 });
      }
    };

    const loop = () => {
      const s = g.current;
      if (!s.running) return;
      s.t++;

      // ---- movement ----
      let mx = 0, my = 0;
      if (s.keys["arrowleft"] || s.keys["a"]) mx -= 1;
      if (s.keys["arrowright"] || s.keys["d"]) mx += 1;
      if (s.keys["arrowup"] || s.keys["w"]) my -= 1;
      if (s.keys["arrowdown"] || s.keys["s"]) my += 1;
      if (s.joy.active) { mx += s.joy.dx; my += s.joy.dy; }
      const ml = Math.hypot(mx, my);
      if (ml > 1) { mx /= ml; my /= ml; }
      const spd = 2.6;
      s.px = Math.max(20, Math.min(WORLD_W - 20, s.px + mx * spd));
      s.py = Math.max(STREET_TOP, Math.min(STREET_BOT - 14, s.py + my * spd));
      s.moving = ml > 0.15;
      if (Math.abs(mx) > 0.2) s.face = mx > 0 ? 1 : -1;
      if (s.moving) s.walk += 0.22;

      // camera follows
      const target = Math.max(0, Math.min(WORLD_W - W, s.px - W / 2));
      s.cam += (target - s.cam) * 0.09;

      // ---- firing (auto-aims at nearest bot) ----
      const wantFire = s.firing || s.keys[" "] || s.keys["j"];
      if (s.fireCd > 0) s.fireCd--;
      if (wantFire && s.fireCd <= 0) {
        s.fireCd = s.rapid > 0 ? 9 : 17;
        let tx = s.px + s.face * 300, ty = s.py, best = 1e9;
        for (const b of s.bots) {
          const d = Math.hypot(b.x - s.px, b.y - s.py);
          if (d < best) { best = d; tx = b.x; ty = b.y; }
        }
        const a = Math.atan2(ty - s.py, tx - s.px);
        s.shots.push({ x: s.px + Math.cos(a) * 16, y: s.py - 6 + Math.sin(a) * 16, vx: Math.cos(a) * 7.5, vy: Math.sin(a) * 7.5, life: 70 });
        if (Math.cos(a) !== 0) s.face = Math.cos(a) > 0 ? 1 : -1;
      }

      // ---- waves & spawning ----
      if (s.toSpawn > 0) {
        s.spawnT--;
        if (s.spawnT <= 0) { spawnBot(); s.toSpawn--; s.spawnT = 46 + Math.random() * 40; }
      }
      const aliveNonBoss = s.bots.filter((b) => !b.boss).length;
      if (s.toSpawn <= 0 && aliveNonBoss === 0 && !s.boss) {
        s.waveT++;
        if (s.waveT > 70) {
          s.waveT = 0; s.wave++;
          if (s.wave >= 4) {
            // BOSS TIME
            const boss: Bot = {
              x: Math.min(WORLD_W - 160, s.px + 380), y: (STREET_TOP + STREET_BOT) / 2,
              hp: 70, maxHp: 70, speed: 0.5, kind: "tv", hit: 0, wob: 0, boss: true, attackCd: 0,
            };
            s.boss = boss; s.bots.push(boss);
            s.toast = "⚠ THE TRASH-BOT HAS AWOKEN ⚠"; s.toastT = 130; s.shake = 16;
          } else {
            s.toSpawn = 3 + s.wave * 2; s.spawnT = 20;
            s.toast = `WAVE ${s.wave} — STAY SHARP!`; s.toastT = 90;
          }
        }
      }
      if (s.boss && s.t % 210 === 0 && s.bots.length < 9) spawnBot(true);

      // ---- bots chase & attack ----
      for (const b of s.bots) {
        const dx = s.px - b.x, dy = s.py - b.y;
        const d = Math.hypot(dx, dy) || 1;
        const stop = b.boss ? 54 : 22;
        if (d > stop) { b.x += (dx / d) * b.speed; b.y += (dy / d) * b.speed; }
        b.wob += 0.15;
        if (b.hit > 0) b.hit--;
        if (b.attackCd > 0) b.attackCd--;
        if (d < (b.boss ? 62 : 26) && b.attackCd <= 0) { b.attackCd = 55; hurtPlayer(); }
      }

      // ---- shots hit bots ----
      for (let i = s.shots.length - 1; i >= 0; i--) {
        const sh = s.shots[i];
        sh.x += sh.vx; sh.y += sh.vy; sh.life--;
        let dead = sh.life <= 0;
        for (let j = s.bots.length - 1; j >= 0; j--) {
          const b = s.bots[j];
          const r = b.boss ? 50 : 20;
          if (Math.hypot(b.x - sh.x, b.y - sh.y) < r) {
            b.hp--; b.hit = 8; dead = true;
            puffBurst(sh.x, sh.y, "#22e58f", 6, 2);
            if (b.hp <= 0) {
              if (b.boss) {
                s.boss = null; s.bots.splice(j, 1);
                s.score += 500; s.shake = 20;
                puffBurst(b.x, b.y, "#ffd23e", 30, 5); puffBurst(b.x, b.y, "#22e58f", 30, 5);
                s.running = false; setFinalScore(s.score + 0); setPhase("won");
              } else killBot(b, j);
            }
            break;
          }
        }
        if (dead) s.shots.splice(i, 1);
      }

      // ---- drops ----
      for (let i = s.drops.length - 1; i >= 0; i--) {
        const dr = s.drops[i];
        dr.life--;
        if (dr.life <= 0) { s.drops.splice(i, 1); continue; }
        if (Math.hypot(dr.x - s.px, dr.y - s.py) < 24) {
          s.toast = DROP_LABEL[dr.kind]; s.toastT = 80;
          if (dr.kind === "sunny") s.rapid = 420;
          else if (dr.kind === "drip") s.hearts = Math.min(5, s.hearts + 1);
          else if (dr.kind === "bin") s.shield = 1;
          else {
            // windy shockwave — knock back + damage everything near
            s.shake = 14;
            for (let j = s.bots.length - 1; j >= 0; j--) {
              const b = s.bots[j];
              const d = Math.hypot(b.x - s.px, b.y - s.py);
              if (d < 190) {
                b.hp -= 2; b.hit = 10;
                const a = Math.atan2(b.y - s.py, b.x - s.px);
                b.x += Math.cos(a) * 70; b.y = Math.max(STREET_TOP, Math.min(STREET_BOT - 10, b.y + Math.sin(a) * 40));
                if (b.hp <= 0 && !b.boss) killBot(b, j);
              }
            }
            puffBurst(s.px, s.py, "#9fd8ff", 26, 6);
          }
          puffBurst(dr.x, dr.y, "#ffd23e", 10, 2);
          s.drops.splice(i, 1);
        }
      }

      // ---- puffs ----
      for (let i = s.puffs.length - 1; i >= 0; i--) {
        const p = s.puffs[i];
        p.x += p.vx; p.y += p.vy; p.vy += 0.05; p.life--;
        if (p.life <= 0) s.puffs.splice(i, 1);
      }

      if (s.rapid > 0) s.rapid--;
      if (s.invuln > 0) s.invuln--;
      if (s.shake > 0) s.shake--;
      if (s.toastT > 0) s.toastT--;

      draw(ctx, s);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); game.running = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <div className="tbg-wrap">
      <div className="tbg-stage">
        <canvas ref={canvasRef} className="tbg-canvas" style={{ aspectRatio: `${W} / ${H}`, touchAction: "none" }} />
        {phase === "ready" && (
          <div className="tbg-overlay">
            <div className="tbg-kicker">★ Eco-Force Arcade ★</div>
            <h3 className="tbg-title">NIGHT<br />PATROL</h3>
            <p className="tbg-sub">
              Roam the glowing streets of NYC as <strong>Earth Jr.</strong> 🌎 Blast trash-bots with the Eco-Beam,
              grab power-ups from your sidekicks — and take down the giant <strong>Trash-Bot</strong>. 🤖
            </p>
            <p className="tbg-sub" style={{ opacity: 0.85 }}>
              🖥️ <strong>WASD / arrows</strong> to move · <strong>SPACE</strong> to fire<br />
              📱 <strong>Left side</strong> = joystick · <strong>right side</strong> = fire
            </p>
            <button className="tbg-btn" onClick={start}>▶ START PATROL</button>
          </div>
        )}
        {phase === "won" && (
          <div className="tbg-overlay tbg-win">
            <div className="tbg-burst">🌎🏆⚡</div>
            <h3 className="tbg-title">THE STREETS ARE SAFE!</h3>
            <p className="tbg-sub">Score: <strong>{finalScore}</strong> · The Trash-Bot is scrap metal. NYC sleeps easy tonight. 💚</p>
            <button className="tbg-btn" onClick={start}>↻ PATROL AGAIN</button>
          </div>
        )}
        {phase === "lost" && (
          <div className="tbg-overlay tbg-lose">
            <div className="tbg-burst">🤖🌃</div>
            <h3 className="tbg-title">EARTH JR. IS DOWN…</h3>
            <p className="tbg-sub">Score: <strong>{finalScore}</strong> · The night isn't over. Get back out there, hero!</p>
            <button className="tbg-btn" onClick={start}>↻ TRY AGAIN</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ================= DRAWING =================

function draw(ctx: CanvasRenderingContext2D, s: any) {
  const ox = s.shake ? (Math.random() - 0.5) * s.shake : 0;
  const oy = s.shake ? (Math.random() - 0.5) * s.shake : 0;
  ctx.save();
  ctx.clearRect(0, 0, W, H);
  ctx.translate(ox, oy);

  // ---- sky ----
  const sky = ctx.createLinearGradient(0, 0, 0, STREET_TOP + 60);
  sky.addColorStop(0, "#0a0e2e"); sky.addColorStop(0.55, "#17224f"); sky.addColorStop(1, "#2c2a5e");
  ctx.fillStyle = sky; ctx.fillRect(-10, -10, W + 20, STREET_TOP + 80);

  // stars
  ctx.fillStyle = "rgba(255,255,255,.7)";
  for (let i = 0; i < STARS.length; i++) {
    const st = STARS[i];
    const tw = 0.35 + 0.65 * Math.abs(Math.sin(s.t * 0.02 + i));
    ctx.globalAlpha = tw * 0.8;
    ctx.fillRect(st.x, st.y, 1.6, 1.6);
  }
  ctx.globalAlpha = 1;

  // moon with glow
  ctx.save();
  ctx.shadowColor = "#cfe4ff"; ctx.shadowBlur = 40;
  ctx.fillStyle = "#e8f1ff";
  ctx.beginPath(); ctx.arc(W - 74, 64, 26, 0, 7); ctx.fill();
  ctx.restore();
  ctx.fillStyle = "rgba(200,215,240,.35)";
  ctx.beginPath(); ctx.arc(W - 82, 58, 5, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.arc(W - 66, 72, 3.5, 0, 7); ctx.fill();

  // ---- far skyline (slow parallax) ----
  drawBuildings(ctx, FAR, s.cam * 0.35, STREET_TOP + 24, "#131a3a", 0.5, s.t, false);
  // ---- near buildings with neon (faster parallax) ----
  drawBuildings(ctx, NEAR, s.cam * 0.72, STREET_TOP + 36, "#1b2352", 1, s.t, true);

  // fog band drifting
  for (let i = 0; i < 3; i++) {
    const fx = ((s.t * (0.2 + i * 0.12) + i * 300) % (W + 360)) - 180;
    const fg = ctx.createRadialGradient(fx, STREET_TOP + 10, 10, fx, STREET_TOP + 10, 150);
    fg.addColorStop(0, "rgba(120,150,220,.10)"); fg.addColorStop(1, "rgba(120,150,220,0)");
    ctx.fillStyle = fg; ctx.fillRect(fx - 160, STREET_TOP - 60, 320, 120);
  }

  // ---- street ----
  const st = ctx.createLinearGradient(0, STREET_TOP, 0, H);
  st.addColorStop(0, "#232746"); st.addColorStop(1, "#101226");
  ctx.fillStyle = st; ctx.fillRect(-10, STREET_TOP + 20, W + 20, H - STREET_TOP);
  // sidewalk edge
  ctx.fillStyle = "#2e3358"; ctx.fillRect(-10, STREET_TOP + 20, W + 20, 8);
  // lane dashes scroll with camera
  ctx.strokeStyle = "rgba(255,214,80,.35)"; ctx.lineWidth = 3; ctx.setLineDash([26, 30]);
  ctx.lineDashOffset = s.cam % 56;
  ctx.beginPath(); ctx.moveTo(-10, STREET_TOP + 118); ctx.lineTo(W + 10, STREET_TOP + 118); ctx.stroke();
  ctx.setLineDash([]);

  // street lamps (world-anchored)
  for (const lx of LAMPS) {
    const x = lx - s.cam;
    if (x < -60 || x > W + 60) continue;
    // light pool
    const pool = ctx.createRadialGradient(x, STREET_TOP + 90, 6, x, STREET_TOP + 90, 96);
    pool.addColorStop(0, "rgba(255,220,130,.16)"); pool.addColorStop(1, "rgba(255,220,130,0)");
    ctx.fillStyle = pool; ctx.beginPath(); ctx.ellipse(x, STREET_TOP + 96, 96, 44, 0, 0, 7); ctx.fill();
    // pole
    ctx.strokeStyle = "#3a4066"; ctx.lineWidth = 4;
    ctx.beginPath(); ctx.moveTo(x, STREET_TOP + 26); ctx.lineTo(x, STREET_TOP - 74); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, STREET_TOP - 74); ctx.lineTo(x + 20, STREET_TOP - 74); ctx.stroke();
    // lamp glow
    ctx.save(); ctx.shadowColor = "#ffd88a"; ctx.shadowBlur = 18;
    ctx.fillStyle = "#ffe9b8"; ctx.beginPath(); ctx.arc(x + 22, STREET_TOP - 72, 6, 0, 7); ctx.fill();
    ctx.restore();
  }

  // glowing curb plants (inspiration: enchanted glow)
  for (let i = 0; i < 10; i++) {
    const x = (i * 275 + 90) - s.cam;
    if (x < -20 || x > W + 20) continue;
    ctx.save(); ctx.shadowColor = "#4be3a0"; ctx.shadowBlur = 10;
    ctx.fillStyle = "#2fbf7f";
    ctx.beginPath(); ctx.ellipse(x, STREET_TOP + 24, 4, 9, -0.3, 0, 7); ctx.fill();
    ctx.beginPath(); ctx.ellipse(x + 6, STREET_TOP + 24, 3.5, 8, 0.3, 0, 7); ctx.fill();
    ctx.restore();
  }

  // ---- entities (sorted by y so lower = in front) ----
  const ents: { y: number; fn: () => void }[] = [];
  for (const dr of s.drops as Drop[]) ents.push({ y: dr.y, fn: () => drawDrop(ctx, dr, s.cam, s.t) });
  for (const b of s.bots as Bot[]) ents.push({ y: b.y, fn: () => (b.boss ? drawBoss(ctx, b, s.cam, s.t) : drawBot(ctx, b, s.cam)) });
  ents.push({ y: s.py, fn: () => drawHero(ctx, s) });
  ents.sort((a, b) => a.y - b.y);
  for (const e of ents) e.fn();

  // shots (over everything)
  for (const sh of s.shots as Shot[]) {
    const x = sh.x - s.cam;
    ctx.save(); ctx.shadowColor = "#22e58f"; ctx.shadowBlur = 12;
    const gr = ctx.createLinearGradient(x - 10, 0, x + 10, 0);
    gr.addColorStop(0, "#3ea6ff"); gr.addColorStop(1, "#22e58f");
    ctx.fillStyle = gr;
    ctx.beginPath(); ctx.ellipse(x, sh.y, 8, 4, Math.atan2(sh.vy, sh.vx), 0, 7); ctx.fill();
    ctx.restore();
  }

  // puffs
  for (const p of s.puffs as Puff[]) {
    ctx.globalAlpha = Math.max(0, p.life / p.max);
    ctx.fillStyle = p.color;
    ctx.beginPath(); ctx.arc(p.x - s.cam, p.y, p.size, 0, 7); ctx.fill();
    ctx.globalAlpha = 1;
  }

  // ---- HUD ----
  // hearts + shield
  ctx.font = "20px serif"; ctx.textAlign = "left"; ctx.textBaseline = "middle";
  ctx.fillText("❤️".repeat(Math.max(0, s.hearts)) + (s.shield ? "🛡️" : ""), 12, 24);
  // score + wave
  ctx.fillStyle = "#fff"; ctx.font = "800 17px 'Bangers', Inter, sans-serif"; ctx.textAlign = "right";
  ctx.fillText(`SCORE ${s.score}`, W - 12, 20);
  ctx.font = "800 13px 'Bangers', Inter, sans-serif";
  ctx.fillStyle = "#9fd8ff";
  ctx.fillText(s.boss ? "☠ BOSS FIGHT" : `WAVE ${s.wave}`, W - 12, 40);
  // rapid-fire timer
  if (s.rapid > 0) {
    ctx.fillStyle = "#ffd23e"; ctx.textAlign = "left"; ctx.font = "800 12px Inter, sans-serif";
    ctx.fillText(`☀️ RAPID ${Math.ceil(s.rapid / 60)}s`, 12, 44);
  }

  // boss HP bar
  if (s.boss) {
    const b = s.boss as Bot;
    ctx.fillStyle = "rgba(0,0,0,.55)"; rr(ctx, W / 2 - 100, 52, 200, 16, 8); ctx.fill();
    ctx.fillStyle = "#e23b3b"; rr(ctx, W / 2 - 97, 55, 194 * (b.hp / b.maxHp), 10, 5); ctx.fill();
    ctx.fillStyle = "#fff"; ctx.font = "800 10px Inter, sans-serif"; ctx.textAlign = "center";
    ctx.fillText("TRASH-BOT", W / 2, 60);
  }

  // toast
  if (s.toastT > 0) {
    ctx.globalAlpha = Math.min(1, s.toastT / 25);
    ctx.fillStyle = "#ffd23e"; ctx.font = "800 21px 'Bangers', Inter, sans-serif"; ctx.textAlign = "center";
    ctx.save(); ctx.shadowColor = "rgba(0,0,0,.8)"; ctx.shadowBlur = 8;
    ctx.fillText(s.toast, W / 2, 96);
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  // virtual joystick UI
  if (s.joy.active) {
    ctx.strokeStyle = "rgba(255,255,255,.35)"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(s.joy.ox, s.joy.oy, 34, 0, 7); ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,.45)";
    ctx.beginPath(); ctx.arc(s.joy.ox + s.joy.dx * 34, s.joy.oy + s.joy.dy * 34, 13, 0, 7); ctx.fill();
  }

  ctx.restore();
}

function drawBuildings(ctx: CanvasRenderingContext2D, list: Building[], cam: number, baseY: number, color: string, alpha: number, t: number, neon: boolean) {
  ctx.globalAlpha = alpha;
  for (const b of list) {
    const x = b.x - cam;
    if (x + b.w < -40 || x > W + 40) continue;
    ctx.fillStyle = color;
    ctx.fillRect(x, baseY - b.h, b.w, b.h);
    // windows
    for (const wd of b.windows) {
      if (!wd.on) continue;
      ctx.fillStyle = neon ? "rgba(255,214,120,.75)" : "rgba(255,214,120,.4)";
      ctx.fillRect(x + wd.wx, baseY - b.h + wd.wy, 5, 7);
    }
    // rooftop water tower silhouette on some
    if (neon && b.w > 110) {
      ctx.fillStyle = "#141a3d";
      ctx.fillRect(x + b.w * 0.6, baseY - b.h - 22, 20, 22);
      ctx.beginPath(); ctx.moveTo(x + b.w * 0.6 - 2, baseY - b.h - 22);
      ctx.lineTo(x + b.w * 0.6 + 10, baseY - b.h - 34); ctx.lineTo(x + b.w * 0.6 + 22, baseY - b.h - 22);
      ctx.closePath(); ctx.fill();
    }
    // neon sign with flicker
    if (neon && b.sign) {
      const flick = Math.sin(t * 0.11 + b.x) > -0.86 ? 1 : 0.25;
      ctx.save();
      ctx.globalAlpha = alpha * flick;
      ctx.shadowColor = b.sign.color; ctx.shadowBlur = 14;
      ctx.fillStyle = b.sign.color;
      ctx.font = "800 13px 'Bangers', Inter, sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(b.sign.text, x + b.w / 2, baseY - b.h + b.sign.y);
      ctx.restore();
    }
  }
  ctx.globalAlpha = 1;
}

function drawHero(ctx: CanvasRenderingContext2D, s: any) {
  const x = s.px - s.cam, y = s.py;
  const bob = s.moving ? Math.sin(s.walk * 2) * 2.2 : Math.sin(s.t * 0.05) * 1.2;
  const blink = s.invuln > 0 && ((s.invuln / 5) | 0) % 2 === 0;
  if (blink) ctx.globalAlpha = 0.45;

  // shadow
  ctx.fillStyle = "rgba(0,0,0,.4)";
  ctx.beginPath(); ctx.ellipse(x, y + 16, 14, 4.5, 0, 0, 7); ctx.fill();

  ctx.save();
  ctx.translate(x, y + bob);
  ctx.scale(s.face, 1);

  // cape flutter
  ctx.fillStyle = "#e23b3b";
  ctx.beginPath();
  ctx.moveTo(-8, -10);
  ctx.quadraticCurveTo(-22 - Math.sin(s.t * 0.15) * 4, 2, -14, 14 + Math.sin(s.t * 0.2) * 2);
  ctx.quadraticCurveTo(-8, 8, -6, 4);
  ctx.closePath(); ctx.fill();

  // legs
  const lw = s.moving ? Math.sin(s.walk * 2) * 5 : 0;
  ctx.strokeStyle = "#1b6b45"; ctx.lineWidth = 4; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(-4, 8); ctx.lineTo(-4 + lw, 16); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(4, 8); ctx.lineTo(4 - lw, 16); ctx.stroke();

  // glowing body — planet Earth Jr.
  ctx.save();
  ctx.shadowColor = "#22e58f"; ctx.shadowBlur = s.shield ? 22 : 12;
  const bodyG = ctx.createRadialGradient(-5, -9, 3, 0, -4, 17);
  bodyG.addColorStop(0, "#5cc9ff"); bodyG.addColorStop(1, "#1f7ae0");
  ctx.fillStyle = bodyG;
  ctx.beginPath(); ctx.arc(0, -4, 15, 0, 7); ctx.fill();
  ctx.restore();
  // continents
  ctx.fillStyle = "#2fbf7f";
  ctx.beginPath(); ctx.ellipse(-6, -9, 6, 4.5, 0.5, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.ellipse(6, 0, 5, 3.6, -0.4, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.ellipse(-3, 4, 3.5, 2.4, 0.2, 0, 7); ctx.fill();
  // face
  ctx.fillStyle = "#0b2540";
  ctx.beginPath(); ctx.arc(3, -7, 2, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.arc(10, -7, 2, 0, 7); ctx.fill();
  ctx.strokeStyle = "#0b2540"; ctx.lineWidth = 1.8;
  ctx.beginPath(); ctx.arc(6.5, -3.5, 4.5, 0.25, Math.PI - 0.4); ctx.stroke();
  // fists
  ctx.fillStyle = "#2fbf7f";
  ctx.beginPath(); ctx.arc(14, 2, 4, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.arc(-12, -2, 4, 0, 7); ctx.fill();

  // shield ring
  if (s.shield) {
    ctx.strokeStyle = "rgba(34,229,143,.7)"; ctx.lineWidth = 2;
    ctx.setLineDash([6, 5]); ctx.lineDashOffset = -s.t * 0.6;
    ctx.beginPath(); ctx.arc(0, -3, 22, 0, 7); ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

function drawBot(ctx: CanvasRenderingContext2D, b: Bot, cam: number) {
  const x = b.x - cam, y = b.y;
  if (x < -50 || x > W + 50) return;
  const wob = Math.sin(b.wob) * 2;

  ctx.fillStyle = "rgba(0,0,0,.4)";
  ctx.beginPath(); ctx.ellipse(x, y + 16, 13, 4, 0, 0, 7); ctx.fill();

  ctx.save();
  ctx.translate(x, y + wob);
  if (b.hit > 0) { ctx.filter = "brightness(2.2)"; }

  // treads
  ctx.fillStyle = "#22243c";
  rr(ctx, -13, 8, 26, 8, 4); ctx.fill();
  // body
  ctx.save(); ctx.shadowColor = "#ff4747"; ctx.shadowBlur = 8;
  ctx.fillStyle = b.kind === "tv" ? "#4b5068" : b.kind === "can" ? "#5c6478" : "#454a61";
  rr(ctx, -12, -16, 24, 26, 5); ctx.fill();
  ctx.restore();
  // face screen
  ctx.fillStyle = "#101223";
  rr(ctx, -9, -13, 18, 12, 3); ctx.fill();
  // angry red eyes
  ctx.fillStyle = "#ff4747";
  ctx.fillRect(-6, -10, 4, 3); ctx.fillRect(2, -10, 4, 3);
  ctx.strokeStyle = "#ff4747"; ctx.lineWidth = 1.6;
  ctx.beginPath(); ctx.moveTo(-5, -4); ctx.lineTo(5, -4); ctx.stroke();
  // antenna
  ctx.strokeStyle = "#7d86a8"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, -16); ctx.lineTo(0, -23); ctx.stroke();
  ctx.fillStyle = "#ff4747"; ctx.beginPath(); ctx.arc(0, -24, 2.4, 0, 7); ctx.fill();
  // junk detail
  ctx.font = "9px serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(b.kind === "tv" ? "📺" : b.kind === "can" ? "🗑️" : "🔋", 0, 5);

  ctx.restore();

  // mini HP pips
  if (b.hp < b.maxHp) {
    ctx.fillStyle = "rgba(0,0,0,.5)"; rr(ctx, x - 12, y - 30, 24, 4, 2); ctx.fill();
    ctx.fillStyle = "#ff7a45"; rr(ctx, x - 11, y - 29, 22 * (b.hp / b.maxHp), 2, 1); ctx.fill();
  }
}

function drawBoss(ctx: CanvasRenderingContext2D, b: Bot, cam: number, t: number) {
  const x = b.x - cam, y = b.y;
  if (x < -140 || x > W + 140) return;
  const wob = Math.sin(t * 0.06) * 3;

  ctx.fillStyle = "rgba(0,0,0,.45)";
  ctx.beginPath(); ctx.ellipse(x, y + 46, 46, 10, 0, 0, 7); ctx.fill();

  ctx.save();
  ctx.translate(x, y + wob);
  if (b.hit > 0) ctx.filter = "brightness(2)";

  // legs
  ctx.strokeStyle = "#2a2e4a"; ctx.lineWidth = 10; ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(-20, 20); ctx.lineTo(-26, 44); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(20, 20); ctx.lineTo(26, 44); ctx.stroke();

  // hulking junk body
  ctx.save(); ctx.shadowColor = "#ff4747"; ctx.shadowBlur = 22;
  ctx.fillStyle = "#3c415c";
  rr(ctx, -42, -34, 84, 60, 10); ctx.fill();
  ctx.restore();
  // plating seams
  ctx.strokeStyle = "#262a44"; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(-42, -8); ctx.lineTo(42, -8); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(0, -34); ctx.lineTo(0, 26); ctx.stroke();
  // junk sticking out
  ctx.font = "15px serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText("📺", -24, 8); ctx.fillText("🔌", 24, 10); ctx.fillText("🗑️", 24, -20); ctx.fillText("🔋", -22, -20);
  // glowing core
  const pulse = 0.6 + 0.4 * Math.sin(t * 0.12);
  ctx.save(); ctx.shadowColor = "#ff4747"; ctx.shadowBlur = 18 * pulse;
  ctx.fillStyle = `rgba(255,71,71,${0.75 + 0.25 * pulse})`;
  ctx.beginPath(); ctx.arc(0, 0, 8 + pulse * 2, 0, 7); ctx.fill();
  ctx.restore();

  // head
  ctx.fillStyle = "#4b5068";
  rr(ctx, -22, -58, 44, 26, 6); ctx.fill();
  ctx.fillStyle = "#101223"; rr(ctx, -17, -54, 34, 14, 3); ctx.fill();
  ctx.fillStyle = "#ff4747";
  ctx.fillRect(-12, -50, 8, 5); ctx.fillRect(4, -50, 8, 5);
  // arms
  ctx.strokeStyle = "#2a2e4a"; ctx.lineWidth = 9;
  const swing = Math.sin(t * 0.05) * 6;
  ctx.beginPath(); ctx.moveTo(-42, -18); ctx.lineTo(-58, 4 + swing); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(42, -18); ctx.lineTo(58, 4 - swing); ctx.stroke();
  ctx.fillStyle = "#22243c";
  ctx.beginPath(); ctx.arc(-58, 6 + swing, 8, 0, 7); ctx.fill();
  ctx.beginPath(); ctx.arc(58, 6 - swing, 8, 0, 7); ctx.fill();

  ctx.restore();
}

function drawDrop(ctx: CanvasRenderingContext2D, d: Drop, cam: number, t: number) {
  const x = d.x - cam;
  if (x < -30 || x > W + 30) return;
  const fy = d.y + Math.sin(t * 0.08 + d.x) * 3;
  const fade = d.life < 120 ? (((d.life / 8) | 0) % 2 === 0 ? 0.35 : 1) : 1;
  ctx.globalAlpha = fade;
  const glow = ctx.createRadialGradient(x, fy, 2, x, fy, 20);
  glow.addColorStop(0, "rgba(255,214,120,.5)"); glow.addColorStop(1, "rgba(255,214,120,0)");
  ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(x, fy, 20, 0, 7); ctx.fill();
  ctx.font = "20px serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText(DROP_EMOJI[d.kind], x, fy);
  ctx.globalAlpha = 1;
}

function rr(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export default EcoNightPatrol;
